import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  RectangleGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  InformationCircleIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Badge } from '../../components/common/Badge';
import { Pagination } from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import { Textarea } from '../../components/common/Textarea';
import { Checkbox } from '../../components/common/Checkbox';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { 
  ProductType, 
  ProductTypeFilters, 
  ProductTypeFormData 
} from '../../types/pim';

export const ProductTypes = () => {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<ProductTypeFilters>({
    page: 1,
    limit: 20,
    sort_by: 'sort_order',
    sort_direction: 'asc'
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedType, setSelectedType] = useState<ProductType | null>(null);
  const [formData, setFormData] = useState<ProductTypeFormData>({
    name: '',
    code: '',
    description: '',
    is_active: true,
    sort_order: 1
  });

  // Fetch product types
  const { data: typesData, isLoading: loadingTypes, error: typesError } = useQuery({
    queryKey: ['pim-product-types', filters],
    queryFn: () => pimService.getProductTypes(filters),
    keepPreviousData: true
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: ProductTypeFormData) => pimService.createProductType(data),
    onSuccess: () => {
      showNotification('Typ produktu został pomyślnie utworzony', 'success');
      queryClient.invalidateQueries(['pim-product-types']);
      setShowCreateModal(false);
      resetForm();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas tworzenia typu produktu', 'error');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ProductTypeFormData> }) => 
      pimService.updateProductType(id, data),
    onSuccess: () => {
      showNotification('Typ produktu został pomyślnie zaktualizowany', 'success');
      queryClient.invalidateQueries(['pim-product-types']);
      setShowEditModal(false);
      resetForm();
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas aktualizacji typu produktu', 'error');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => pimService.deleteProductType(id),
    onSuccess: () => {
      showNotification('Typ produktu został pomyślnie usunięty', 'success');
      queryClient.invalidateQueries(['pim-product-types']);
      setShowDeleteModal(false);
      setSelectedType(null);
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas usuwania typu produktu', 'error');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      is_active: true,
      sort_order: 1
    });
    setSelectedType(null);
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value || undefined,
      page: 1
    }));
  };

  const handleFilterChange = (key: keyof ProductTypeFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handleSort = (sortBy: string) => {
    setFilters(prev => ({
      ...prev,
      sort_by: sortBy,
      sort_direction: prev.sort_by === sortBy && prev.sort_direction === 'asc' ? 'desc' : 'asc',
      page: 1
    }));
  };

  const handleCreate = () => {
    setShowCreateModal(true);
    resetForm();
  };

  const handleEdit = (type: ProductType) => {
    setSelectedType(type);
    setFormData({
      name: type.name || '',
      code: type.code || '',
      description: type.description || '',
      is_active: Boolean(type.is_active),
      sort_order: type.sort_order || 1
    });
    setShowEditModal(true);
  };

  const handleDelete = (type: ProductType) => {
    setSelectedType(type);
    setShowDeleteModal(true);
  };

  const handleSubmit = () => {
    if (showEditModal && selectedType) {
      updateMutation.mutate({ id: selectedType.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sort_by: 'sort_order',
      sort_direction: 'asc'
    });
  };

  // Auto-generate code from name
  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      code: name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    }));
  };

  if (!permissions.canManageProductTypes) {
    return (
      <div className="text-center py-12">
        <RectangleGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.common.accessDenied')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('pim.common.accessDeniedDescription')}
        </p>
      </div>
    );
  }

  if (typesError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{t('pim.common.loadingError')}: {(typesError as any).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100">
            <RectangleGroupIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('pim.productTypes.title')}</h1>
            <p className="text-gray-600">{t('pim.productTypes.subtitle')}</p>
          </div>
        </div>
        {permissions.canManageProductTypes && (
          <Button icon={PlusIcon} onClick={handleCreate}>
            {t('pim.productTypes.create')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card role="region" aria-label="Product types filters">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5" aria-hidden="true" />
            {t('pim.productTypes.filters')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder={t('pim.productTypes.searchPlaceholder')}
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                icon={MagnifyingGlassIcon}
                aria-label="Search product types"
              />
            </div>
            <div className="flex gap-2">
              <Select
                value={filters.is_active?.toString() || ''}
                onChange={(e) => handleFilterChange('is_active', e.target.value ? e.target.value === 'true' : undefined)}
                options={[
                  { value: '', label: t('pim.common.allStatuses') },
                  { value: 'true', label: t('common.status.active') },
                  { value: 'false', label: t('common.status.inactive') }
                ]}
                aria-label="Filter by status"
              />
              {(filters.search || filters.is_active !== undefined) && (
                <Button
                  variant="outline"
                  icon={XMarkIcon}
                  onClick={clearFilters}
                  aria-label="Clear all filters"
                  title="Clear all filters"
                >
                  {t('common.clear')}
                </Button>
              )}
            </div>
          </div>

          {/* Filter summary */}
          {(filters.search || filters.is_active !== undefined) && (
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
              <InformationCircleIcon className="h-4 w-4" />
              <span>
                {t('pim.productTypes.showingResults')}
                {filters.search && ` ${t('pim.productTypes.forQuery')} "${filters.search}"`}
                {filters.is_active !== undefined && ` (${filters.is_active ? t('common.status.active') : t('common.status.inactive')} ${t('pim.productTypes.onlyLabel')})`}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Types Table */}
      <Card role="region" aria-label="Product types list">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RectangleGroupIcon className="h-5 w-5" aria-hidden="true" />
              {t('pim.productTypes.title')}
              {typesData?.pagination && (
                <Badge variant="secondary" className="ml-2">
                  {typesData.pagination.total} total
                </Badge>
              )}
            </div>
            {typesData?.data?.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <ArrowsUpDownIcon className="h-4 w-4" />
                {t('pim.productTypes.sortHint')}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTypes ? (
            <div className="text-center py-12" role="status" aria-label="Loading product types">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">{t('pim.productTypes.loading')}</p>
            </div>
          ) : !typesData?.data?.length ? (
            <div className="text-center py-12">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100">
                <RectangleGroupIcon className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {(filters.search || filters.is_active !== undefined)
                  ? t('pim.productTypes.noTypes')
                  : t('pim.productTypes.noTypes')
                }
              </h3>
              <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                {(filters.search || filters.is_active !== undefined)
                  ? t('pim.productTypes.noResultsHint')
                  : t('pim.productTypes.noTypesDescription')
                }
              </p>
              {permissions.canManageProductTypes && (
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {(filters.search || filters.is_active !== undefined) && (
                    <Button variant="outline" icon={XMarkIcon} onClick={clearFilters}>
                      {t('pim.products.clearFilters')}
                    </Button>
                  )}
                  <Button icon={PlusIcon} onClick={handleCreate}>
                    {(filters.search || filters.is_active !== undefined)
                      ? t('pim.productTypes.create')
                      : t('pim.productTypes.create')
                    }
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" role="table" aria-label="Product types table">
                <thead className="bg-gray-50">
                  <tr role="row">
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                      onClick={() => handleSort('sort_order')}
                      role="columnheader"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort('sort_order');
                        }
                      }}
                      aria-sort={filters.sort_by === 'sort_order' ? (filters.sort_direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title="Click to sort by sort order"
                    >
                      <div className="flex items-center gap-1">
                        Kolejność sortowania
                        {filters.sort_by === 'sort_order' && (
                          <ArrowsUpDownIcon className={`h-3 w-3 transform transition-transform ${
                            filters.sort_direction === 'desc' ? 'rotate-180' : ''
                          }`} aria-hidden="true" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                      onClick={() => handleSort('name')}
                      role="columnheader"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort('name');
                        }
                      }}
                      aria-sort={filters.sort_by === 'name' ? (filters.sort_direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title="Click to sort by name"
                    >
                      <div className="flex items-center gap-1">
                        {t('pim.productTypes.name')}
                        {filters.sort_by === 'name' && (
                          <ArrowsUpDownIcon className={`h-3 w-3 transform transition-transform ${
                            filters.sort_direction === 'desc' ? 'rotate-180' : ''
                          }`} aria-hidden="true" />
                        )}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                      onClick={() => handleSort('code')}
                      role="columnheader"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort('code');
                        }
                      }}
                      aria-sort={filters.sort_by === 'code' ? (filters.sort_direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title="Click to sort by code"
                    >
                      <div className="flex items-center gap-1">
                        {t('pim.productTypes.code')}
                        {filters.sort_by === 'code' && (
                          <ArrowsUpDownIcon className={`h-3 w-3 transform transition-transform ${
                            filters.sort_direction === 'desc' ? 'rotate-180' : ''
                          }`} aria-hidden="true" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" role="columnheader">
                      {t('pim.productTypes.description')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" role="columnheader">
                      {t('pim.productTypes.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" role="columnheader">
                      {t('pim.productTypes.products')}
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-150"
                      onClick={() => handleSort('created_at')}
                      role="columnheader"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort('created_at');
                        }
                      }}
                      aria-sort={filters.sort_by === 'created_at' ? (filters.sort_direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title="Click to sort by creation date"
                    >
                      <div className="flex items-center gap-1">
                        {t('pim.productTypes.created')}
                        {filters.sort_by === 'created_at' && (
                          <ArrowsUpDownIcon className={`h-3 w-3 transform transition-transform ${
                            filters.sort_direction === 'desc' ? 'rotate-180' : ''
                          }`} aria-hidden="true" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" role="columnheader">
                      {t('common.actions.title')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" role="rowgroup">
                  {typesData.data.map((type) => (
                    <tr key={type.id} className="hover:bg-gray-50 transition-colors duration-150" role="row">
                      <td className="px-6 py-4 whitespace-nowrap" role="cell">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                          {type.sort_order}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" role="cell">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100">
                              <RectangleGroupIcon className="h-4 w-4 text-blue-600" aria-hidden="true" />
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-900">{type.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" role="cell">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono">{type.code}</code>
                      </td>
                      <td className="px-6 py-4" role="cell">
                        <div className="text-sm text-gray-500 max-w-xs">
                          {type.description ? (
                            <span title={type.description} className="truncate block">
                              {type.description}
                            </span>
                          ) : (
                            <span className="italic text-gray-400">{t('common.placeholders.none')}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" role="cell">
                        {type.is_active ? (
                          <Badge variant="success" icon={CheckCircleIcon}>{t('common.status.active')}</Badge>
                        ) : (
                          <Badge variant="secondary" icon={XCircleIcon}>{t('common.status.inactive')}</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" role="cell">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {type.products?.length || 0}
                          </span>
                          {type.products?.length > 0 && (
                            <span className="text-xs text-gray-500">{t('pim.productTypes.products').toLowerCase()}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" role="cell">
                        <div className="flex flex-col">
                          <span>
                            {type.created_at && !isNaN(new Date(type.created_at).getTime())
                              ? new Date(type.created_at).toLocaleDateString()
                              : t('common.placeholders.na')
                            }
                          </span>
                          <span className="text-xs text-gray-400">
                            {type.created_at && !isNaN(new Date(type.created_at).getTime())
                              ? new Date(type.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              : ''
                            }
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" role="cell">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={PencilIcon}
                            onClick={() => handleEdit(type)}
                            aria-label={`Edit product type ${type.name}`}
                            title={`Edit ${type.name}`}
                          >
                            {t('common.actions.edit')}
                          </Button>
                          {permissions.canManageProductTypes && (
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={TrashIcon}
                              onClick={() => handleDelete(type)}
                              aria-label={`Delete product type ${type.name}`}
                              title={`Delete ${type.name}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              {t('common.actions.delete')}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {typesData?.pagination && (
            <div className="mt-6">
              <Pagination
                currentPage={typesData.pagination.page}
                totalPages={typesData.pagination.totalPages}
                totalItems={typesData.pagination.total}
                itemsPerPage={typesData.pagination.limit}
                onPageChange={(page) => handleFilterChange('page', page)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        title={showEditModal ? t('pim.productTypes.edit') : t('pim.productTypes.create')}
        size="lg"
      >
        <div className="space-y-6">
          {/* Form Header */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100">
                <RectangleGroupIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900">
                {showEditModal ? t('pim.productTypes.edit') : t('pim.productTypes.create')}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {showEditModal
                  ? t('pim.productTypes.editHint')
                  : t('pim.productTypes.subtitle')
                }
              </p>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={`${t('pim.productTypes.name')} *`}
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                required
                placeholder={t('pim.productTypes.namePlaceholder')}
                help={t('pim.productTypes.nameHelp')}
                aria-describedby="name-help"
              />
              <Input
                label={`${t('pim.productTypes.code')} *`}
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                required
                placeholder={t('pim.productTypes.codePlaceholder')}
                help={t('pim.productTypes.codeHelp')}
                aria-describedby="code-help"
              />
            </div>

            <Textarea
              label={t('pim.productTypes.description')}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('pim.productTypes.descriptionPlaceholder')}
              rows={3}
              help={t('pim.productTypes.descriptionHelp')}
              aria-describedby="description-help"
            />

            <Input
              label="Kolejność sortowania"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
              placeholder="1"
              help="Określa kolejność wyświetlania typów produktów (niższe liczby są wyświetlane pierwsze)"
            />

            <div className="bg-gray-50 p-4 rounded-lg">
              <Checkbox
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                label={t('pim.productTypes.status')}
              />
              <p className="mt-1 ml-6 text-sm text-gray-500">
                {t('pim.productTypes.activeHelp')}
              </p>
              {!formData.is_active && (
                <div className="mt-2 text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                  <InformationCircleIcon className="h-4 w-4 inline mr-1" />
                  {t('pim.productTypes.inactiveWarning')}
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                resetForm();
              }}
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isLoading || updateMutation.isLoading || !formData.name.trim() || !formData.code.trim()}
              icon={(createMutation.isLoading || updateMutation.isLoading) ? undefined : CheckIcon}
            >
              {(createMutation.isLoading || updateMutation.isLoading)
                ? t('pim.common.saving')
                : showEditModal ? t('common.actions.save') : t('pim.productTypes.create')
              }
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedType(null);
        }}
        title={t('pim.productTypes.delete')}
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('pim.productTypes.delete')}
              </h3>
              <p className="text-sm text-gray-600">
                {t('pim.productTypes.confirmDelete', { name: selectedType?.name })}
              </p>
            </div>
          </div>

          {(selectedType?.products?.length || 0) > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" aria-hidden="true" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-yellow-800 mb-1">
                    {t('pim.productTypes.impactWarning')}
                  </h4>
                  <p className="text-sm text-yellow-700">
                    {t('pim.productTypes.deleteWarning', { count: selectedType?.products?.length })}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedType(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => selectedType && deleteMutation.mutate(selectedType.id)}
              disabled={deleteMutation.isLoading}
              icon={deleteMutation.isLoading ? undefined : TrashIcon}
            >
              {deleteMutation.isLoading ? t('pim.common.deleting') : t('pim.productTypes.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
